// APIKeysSection.jsx
// =============================================
// Facility Management Dashboard v3 APIKeys Section – Lets the user input and manage API keys securely
// Keys are only saved when validated, with a clear two-step deletion process for safety
// =============================================
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { keyStateManager } from '@/managers/zKeysManager';
import { botManager } from '@/managers/BotManager';
import { AlertTriangle, Check, ExternalLink, Github, Key, Lock, RefreshCw, X } from 'lucide-react';
import { testApiKey } from './TestTheAPI';
import { useConversation } from '@/managers/ConvoManager';
import { DEFAULT_PROMPTED_BOT_ID } from '@/utils/defaultBotProfile';
import { CustomEndpointsSection } from './CustomEndpointsSection';

const API_PROVIDERS = [
  // --- Active Providers ---
  {
    id: 'CLAUDE',
    name: 'Claude',
    keyPrefix: 'sk-a',
    description: 'Anthropic Claude Models',
    getKeyUrl: 'https://console.anthropic.com/',
    pricingUrl: 'https://anthropic.com/pricing',
    color: '#F6A192'
  },
  {
    id: 'OPENAI',
    name: 'OpenAI',
    keyPrefix: 'sk-',
    description: 'OpenAI GPT Models',
    getKeyUrl: 'https://platform.openai.com/api-keys',
    pricingUrl: 'https://openai.com/pricing',
    color: '#10B981'
  },
  {
    id: 'GEMINI',
    name: 'Gemini',
    keyPrefix: 'AIza',
    description: 'Gemini Pro, Flash and Exp Models',
    getKeyUrl: 'https://ai.google.dev/tutorials/setup',
    pricingUrl: 'https://ai.google.dev/pricing',
    color: '#A78BFA'
  },
  {
    id: 'MISTRAL',
    name: 'Mistral',
    keyPrefix: '', // Mistral keys are long random strings; empty prefix defers to testApiKey
    description: 'Mistral AI Models',
    getKeyUrl: 'https://console.mistral.ai/',
    pricingUrl: 'https://docs.mistral.ai/platform/pricing/',
    color: '#FF7F0E'
  },
  {
    id: 'OPENROUTER',
    name: 'OpenRouter',
    keyPrefix: 'sk-or',
    description: 'Multi-Model API Hub',
    getKeyUrl: 'https://openrouter.ai/keys',
    pricingUrl: 'https://openrouter.ai/docs#models',
    color: '#7C3AED'
  },
  {
    id: 'GROK',
    name: 'Grok (xAI)',
    keyPrefix: 'xai-',
    description: "xAI's Grok Models",
    getKeyUrl: 'https://console.x.ai/',
    pricingUrl: 'https://x.ai/developers',
    color: '#1DA1F2'
  },
];

// Helper to determine the initial state based on the key value
const getInitialState = (value) => (value ? 'locked' : 'empty');

function GitHubIntegrationCard({ storedValue, onStoredValueChange }) {
  const [token, setToken] = useState(storedValue || '');
  const [lastSavedValue, setLastSavedValue] = useState(storedValue || '');
  const [status, setStatus] = useState(storedValue ? 'saved' : 'idle');

  useEffect(() => {
    const nextValue = storedValue || '';
    setToken(nextValue);
    setLastSavedValue(nextValue);
    setStatus(nextValue ? 'saved' : 'idle');
  }, [storedValue]);

  const isDirty = token !== lastSavedValue;

  const handleSave = async () => {
    if (status === 'saving') {
      return;
    }

    setStatus('saving');

    try {
      const trimmedToken = token.trim();

      if (!trimmedToken) {
        await keyStateManager.deleteKey('GITHUB');
        onStoredValueChange('');
        setToken('');
        setLastSavedValue('');
        setStatus('idle');
        return;
      }

      await keyStateManager.updateKeys({ GITHUB: trimmedToken });
      onStoredValueChange(trimmedToken);
      setToken(trimmedToken);
      setLastSavedValue(trimmedToken);
      setStatus('saved');
    } catch (error) {
      setStatus('error');
    }
  };

  const handleClear = async () => {
    if (status === 'saving') {
      return;
    }

    setStatus('saving');

    try {
      await keyStateManager.deleteKey('GITHUB');
      onStoredValueChange('');
      setToken('');
      setLastSavedValue('');
      setStatus('idle');
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <div className="p-4 sm:p-5 rounded-xl border border-border-subtle bg-surface-raised space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <Github className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-medium text-on-surface">GitHub Integration</h3>
            <p className="text-sm text-on-surface-dim">Add a token so you can clone repos into your workspace.</p>
          </div>
        </div>
        <a href="https://github.com/settings/personal-access-tokens/" target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:text-primary-light hover:underline whitespace-nowrap flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors" title="Get a GitHub PAT">Get Token <ExternalLink className="w-3 h-3" /></a>
      </div>

      <div className="space-y-2">
        <label htmlFor="githubPat" className="block text-sm font-medium text-on-surface">GitHub Personal Access Token</label>
        <input
          id="githubPat"
          type="password"
          value={token}
          onChange={(event) => {
            setToken(event.target.value);
            if (status !== 'saving' && status !== 'error') {
              setStatus('idle');
            }
          }}
          placeholder="ghp_..."
          className="w-full px-3 py-2.5 bg-surface border border-border-color rounded-lg text-on-surface placeholder:text-on-surface-dim/50 focus:outline-none focus:ring-1 focus:ring-primary"
          autoComplete="off"
          spellCheck="false"
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-xs">
          {status === 'saving' && <span className="text-on-surface-dim">Saving token...</span>}
          {status === 'saved' && !isDirty && <span className="text-success inline-flex items-center gap-1"><Check className="w-3 h-3" /> Saved</span>}
          {status === 'error' && <span className="text-error inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Could not save token</span>}
          {status === 'idle' && isDirty && <span className="text-on-surface-dim">Unsaved changes</span>}
          {status === 'idle' && !isDirty && !lastSavedValue && <span className="text-on-surface-dim">No token saved</span>}
        </div>

        <div className="flex items-center gap-2">
          {(token || lastSavedValue) && (
            <button
              type="button"
              onClick={handleClear}
              disabled={status === 'saving'}
              className="px-3 py-2 rounded-lg border border-border-subtle text-sm text-on-surface-dim hover:text-on-surface hover:border-border-color transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={status === 'saving' || !isDirty}
            className="px-3 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {status === 'saving' && <RefreshCw className="w-4 h-4 animate-spin" />}
            Save Token
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========================
 * APIKeyCard - Mobile-responsive card component for individual API key management
 * Replaces table rows with stacked card layout on mobile
 * ======================== */
function APIKeyCard({ provider, value, onValueChange, createConversation, onClose }) {
  const [keyStatus, setKeyStatus] = useState({ state: getInitialState(value), message: '' });
  const [isLoading, setIsLoading] = useState(false);

  // Effect to sync status if the key is changed externally (e.g., loaded from DB)
  useEffect(() => {
    const initialState = getInitialState(value);
    if (keyStatus.state !== 'editing' && keyStatus.state !== 'testing') {
      setKeyStatus({ state: initialState, message: '' });
    }
  }, [value]);

  const isValidKeyFormat = (key) => {
    if (!key) return false;
    if (!provider.keyPrefix) return true;
    return key.startsWith(provider.keyPrefix);
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    onValueChange(newValue); // Notify parent of the new value
    if (keyStatus.state !== 'editing') {
      setKeyStatus({ state: 'editing', message: '' });
    }
  };

  const handleTestKey = async () => {
    if (!isValidKeyFormat(value)) {
      setKeyStatus({
        state: 'invalid',
        message: `Invalid ${provider.name} API key format.${provider.keyPrefix ? ` Keys must start with "${provider.keyPrefix}".` : ' Please enter a valid key.'}`
      });
      return;
    }

    setIsLoading(true);
    setKeyStatus({ state: 'testing', message: '' });

    try {
      const wasFirstKey = !botManager.getState().hasPlatformKeys;
      const result = await testApiKey(provider.id, value);
      
      if (result.valid) {
        await keyStateManager.updateKeys({ [provider.id]: value });
        setKeyStatus({ state: 'locked', message: '' });

        if (wasFirstKey && createConversation && onClose) {
          const defaultBot = botManager.getBotById(DEFAULT_PROMPTED_BOT_ID);
          const modelForNewPlatform = defaultBot?.variations?.find(m => m.platform.toUpperCase() === provider.id);
          
          if (defaultBot && modelForNewPlatform) {
            onClose(); // Close the settings panel
            // Create a new conversation with the default bot and a model matching the new key
            await createConversation({ botId: defaultBot.id, modelId: modelForNewPlatform.id });
          }
        }
      } else {
        setKeyStatus({ state: 'invalid', message: result.message || 'Invalid API key. Please check the key and try again.' });
      }
    } catch (error) {
      setKeyStatus({ state: 'invalid', message: error.message || 'An unexpected error occurred during testing.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    await keyStateManager.deleteKey(provider.id);
    onValueChange(''); // Notify parent that value is now empty
  };

  const getMaskedKey = () => {
    if (!value) return '';
    const MAX_DOTS = 14; const prefixChars = 4; const suffixChars = 4;
    if (value.length <= prefixChars + suffixChars) return '•'.repeat(value.length);
    return `${value.substring(0, prefixChars)}${'•'.repeat(MAX_DOTS)}${value.substring(value.length - suffixChars)}`;
  };
  
  const renderKeyInput = () => {
    if (keyStatus.state === 'locked' || keyStatus.state === 'unlocking') {
      const isUnlocking = keyStatus.state === 'unlocking';
      return (
        <div className={`relative w-full pl-3 pr-16 py-3 sm:py-2 bg-surface border rounded-lg ${isUnlocking ? 'border-error' : 'border-success'} flex items-center min-h-[44px] sm:min-h-[40px]`}>
          <span className="truncate">{getMaskedKey()}</span>
          <div className="absolute right-3 flex items-center gap-2">
            {isUnlocking ? (
              <>
                <button type="button" onClick={handleDelete} className="text-error hover:text-error-dark transition-colors p-1" title="Delete API key"><X className="w-4 h-4" /></button>
                <button type="button" onClick={() => setKeyStatus({ state: 'locked', message: '' })} className="text-on-surface-dim hover:text-on-surface transition-colors p-1" title="Cancel deletion"><Lock className="w-4 h-4" /></button>
              </>
            ) : (
              <button type="button" onClick={() => setKeyStatus({ state: 'unlocking', message: '' })} className="text-success hover:text-primary transition-colors p-1" title="Unlock to delete"><Lock className="w-4 h-4" /></button>
            )}
          </div>
        </div>
      );
    }
    return (
      <input type="password" value={value} onChange={handleChange} onFocus={() => keyStatus.state === 'locked' && setKeyStatus({ state: 'editing', message: '' })} disabled={provider.disabled || isLoading} placeholder={provider.disabled ? 'Coming Soon' : (provider.keyPrefix ? `${provider.keyPrefix}...` : 'Enter API Key...')} className={`w-full pl-3 pr-3 py-3 sm:py-2 bg-surface border ${keyStatus.state === 'invalid' ? 'border-error' : 'border-border-subtle'} rounded-lg text-sm text-on-surface placeholder:text-on-surface-dim focus:outline-none focus:ring-1 focus:ring-primary/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] sm:min-h-[40px]`} autoComplete="off" spellCheck="false" />
    );
  };

  const renderStatusAndActions = () => {
    if (provider.disabled) {
      return (<div className="flex items-center justify-between"><div className="text-xs text-on-surface-dim">Coming Soon</div></div>);
    }
    let statusElement = null;
    let actionElement = null;
    switch (keyStatus.state) {
      case 'empty': statusElement = <div className="text-xs text-on-surface-dim">No key</div>; break;
      case 'editing':
        if (!value) statusElement = <div className="text-xs text-on-surface-dim">Enter key</div>;
        else if (isValidKeyFormat(value)) statusElement = (<button className="text-xs text-primary hover:text-primary-light flex items-center gap-1 px-3 py-2 rounded-lg bg-primary/10 hover:bg-primary/20 transition-colors min-h-[36px]" onClick={handleTestKey} disabled={isLoading}>Test Key</button>);
        else statusElement = <div className="text-xs text-on-surface-dim">Format invalid</div>;
        break;
      case 'testing': statusElement = (<div className="flex items-center gap-1 text-xs text-on-surface-dim"><RefreshCw className="w-3 h-3 animate-spin" /><span>Testing...</span></div>); break;
      case 'invalid': statusElement = (<button className="text-xs text-error hover:text-error-dark flex items-center gap-1 px-3 py-2 rounded-lg bg-error/10 hover:bg-error/20 transition-colors min-h-[36px]" onClick={handleTestKey} disabled={isLoading || !isValidKeyFormat(value)}><RefreshCw className="w-3 h-3" /><span>Retest Key</span></button>); break;
      case 'locked': statusElement = (<div className="flex items-center gap-1 text-xs text-success"><Check className="w-3 h-3" /><span>Valid</span></div>); break;
      case 'unlocking': statusElement = (<div className="flex items-center gap-1 text-xs text-error"><span>Ready to delete</span></div>); break;
      default: break;
    }
    if (!provider.disabled) {
      actionElement = (<a href={provider.getKeyUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:text-primary-light hover:underline whitespace-nowrap flex items-center gap-1 px-3 py-2 rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors min-h-[36px]" title={`Get a ${provider.name} API key`}>Get Key <ExternalLink className="w-3 h-3" /></a>);
    }
    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
        <div className="flex-1">{statusElement}</div>
        {actionElement && (<div className="flex-shrink-0">{actionElement}</div>)}
      </div>
    );
  };

  const displayDescription = provider.description.startsWith('(Coming Soon') ? provider.description.substring(provider.description.indexOf(') - ') + 4) : provider.description;

  return (
    <div className={`p-4 rounded-xl border border-border-subtle bg-surface space-y-4 ${provider.dimmed ? 'opacity-60' : ''}`}>
      <div className="flex items-center gap-3">
        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: provider.color }} />
        <div className="flex-1 min-w-0">
          <div className="text-md font-medium text-on-surface">{provider.name}</div>
          <div className="text-xs text-on-surface-dim">{displayDescription}</div>
        </div>
      </div>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-on-surface">API Key</label>
        {renderKeyInput()}
        {keyStatus.state === 'invalid' && keyStatus.message && (
          <div className="text-xs text-error mt-1">{keyStatus.message}</div>
        )}
      </div>
      <div>{renderStatusAndActions()}</div>
    </div>
  );
}

/* ========================
 * APIKeyField - Original table row component for desktop
 * ======================== */
function APIKeyField({ provider, value, onValueChange, createConversation, onClose }) {
  const [keyStatus, setKeyStatus] = useState({ state: getInitialState(value), message: '' });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const initialState = getInitialState(value);
    if (keyStatus.state !== 'editing' && keyStatus.state !== 'testing') {
      setKeyStatus({ state: initialState, message: '' });
    }
  }, [value]);

  const isValidKeyFormat = (key) => {
    if (!key) return false;
    if (!provider.keyPrefix) return true;
    return key.startsWith(provider.keyPrefix);
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    onValueChange(newValue);
    if (keyStatus.state !== 'editing') {
      setKeyStatus({ state: 'editing', message: '' });
    }
  };

  const handleTestKey = async () => {
    if (!isValidKeyFormat(value)) {
      setKeyStatus({ state: 'invalid', message: `Invalid ${provider.name} API key format.${provider.keyPrefix ? ` Keys must start with "${provider.keyPrefix}".` : ' Please enter a valid key.'}` });
      return;
    }
    setIsLoading(true);
    setKeyStatus({ state: 'testing', message: '' });
    try {
      const wasFirstKey = !botManager.getState().hasPlatformKeys;
      const result = await testApiKey(provider.id, value);
      
      if (result.valid) {
        await keyStateManager.updateKeys({ [provider.id]: value });
        setKeyStatus({ state: 'locked', message: '' });

        if (wasFirstKey && createConversation && onClose) {
          const defaultBot = botManager.getBotById(DEFAULT_PROMPTED_BOT_ID);
          const modelForNewPlatform = defaultBot?.variations?.find(m => m.platform.toUpperCase() === provider.id);
          
          if (defaultBot && modelForNewPlatform) {
            onClose();
            await createConversation({ botId: defaultBot.id, modelId: modelForNewPlatform.id });
          }
        }
      } else {
        setKeyStatus({ state: 'invalid', message: result.message || 'Invalid API key. Please check the key and try again.' });
      }
    } catch (error) {
      setKeyStatus({ state: 'invalid', message: error.message || 'An unexpected error occurred during testing.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    await keyStateManager.deleteKey(provider.id);
    onValueChange('');
  };

  const getMaskedKey = () => {
    if (!value) return '';
    const MAX_DOTS = 14; const prefixChars = 4; const suffixChars = 4;
    if (value.length <= prefixChars + suffixChars) return '•'.repeat(value.length);
    return `${value.substring(0, prefixChars)}${'•'.repeat(MAX_DOTS)}${value.substring(value.length - suffixChars)}`;
  };
  
  const renderKeyInput = () => {
    if (keyStatus.state === 'locked' || keyStatus.state === 'unlocking') {
      const isUnlocking = keyStatus.state === 'unlocking';
      return (
        <div className={`relative w-full pl-3 pr-16 py-2 bg-surface border rounded-lg ${isUnlocking ? 'border-error' : 'border-success'} flex items-center h-10`}>
          <span className="truncate">{getMaskedKey()}</span>
          <div className="absolute right-3 flex items-center gap-2">
            {isUnlocking ? (
              <>
                <button type="button" onClick={handleDelete} className="text-error hover:text-error-dark transition-colors" title="Delete API key"><X className="w-4 h-4" /></button>
                <button type="button" onClick={() => setKeyStatus({ state: 'locked', message: '' })} className="text-on-surface-dim hover:text-on-surface transition-colors" title="Cancel deletion"><Lock className="w-4 h-4" /></button>
              </>
            ) : (
              <button type="button" onClick={() => setKeyStatus({ state: 'unlocking', message: '' })} className="text-success hover:text-primary transition-colors" title="Unlock to delete"><Lock className="w-4 h-4" /></button>
            )}
          </div>
        </div>
      );
    }
    return (
      <input type="password" value={value} onChange={handleChange} onFocus={() => keyStatus.state === 'locked' && setKeyStatus({ state: 'editing', message: '' })} disabled={provider.disabled || isLoading} placeholder={provider.disabled ? 'Coming Soon' : (provider.keyPrefix ? `${provider.keyPrefix}...` : 'Enter API Key...')} className={`w-full pl-3 pr-3 py-2 bg-surface border ${keyStatus.state === 'invalid' ? 'border-error' : 'border-border-subtle'} rounded-lg text-sm text-on-surface placeholder:text-on-surface-dim focus:outline-none focus:ring-1 focus:ring-primary/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors`} autoComplete="off" spellCheck="false" />
    );
  };

  const renderStatus = () => {
    if (provider.disabled) {
      return <div className="text-xs text-on-surface-dim">Coming Soon</div>;
    }
    switch (keyStatus.state) {
      case 'empty': return <div className="text-xs text-on-surface-dim">No key</div>;
      case 'editing':
        if (!value) return <div className="text-xs text-on-surface-dim">Enter key</div>;
        if (isValidKeyFormat(value)) return (<button className="text-xs text-primary hover:text-primary-light flex items-center gap-1" onClick={handleTestKey} disabled={isLoading}>Test Key</button>);
        return <div className="text-xs text-on-surface-dim">Format invalid</div>;
      case 'testing': return (<div className="flex items-center gap-1 text-xs text-on-surface-dim"><RefreshCw className="w-3 h-3 animate-spin" /><span>Testing...</span></div>);
      case 'invalid': return (<button className="text-xs text-error hover:text-error-dark flex items-center gap-1" onClick={handleTestKey} disabled={isLoading || !isValidKeyFormat(value)}><RefreshCw className="w-3 h-3" /><span>Retest Key</span></button>);
      case 'locked': return (<div className="flex items-center gap-1 text-xs text-success"><Check className="w-3 h-3" /><span>Valid</span></div>);
      case 'unlocking': return (<div className="flex items-center gap-1 text-xs text-error"><span>Ready to delete</span></div>);
      default: return null;
    }
  };

  const displayDescription = provider.description.startsWith('(Coming Soon') ? provider.description.substring(provider.description.indexOf(') - ') + 4) : provider.description;

  return (
    <tr className={`${provider.dimmed ? 'opacity-60' : ''}`}>
      <td className="py-3 pl-6 pr-2">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: provider.color }} />
          <div>
            <div className="text-md font-medium text-on-surface">{provider.name}</div>
            <div className="text-xs text-on-surface-dim">{displayDescription}</div>
          </div>
        </div>
      </td>
      <td className="py-3 px-4 relative">
        <div className="relative">{renderKeyInput()}</div>
        {keyStatus.state === 'invalid' && keyStatus.message && (<div className="text-xs text-error mt-1">{keyStatus.message}</div>)}
      </td>
      <td className="py-3 px-6 min-w-[120px]">{renderStatus()}</td>
      <td className="py-3 px-6 text-right">
        {!provider.disabled && (
          <a href={provider.getKeyUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:text-primary-light hover:underline whitespace-nowrap flex items-center gap-1 justify-end" title={`Get a ${provider.name} API key`} >Get Key <ExternalLink className="w-3 h-3" /></a>
        )}
      </td>
    </tr>
  );
}

export function APIKeysSection({ onClose }) {
  const [keys, setKeys] = useState({ CLAUDE: '', OPENAI: '', GEMINI: '', MISTRAL: '', OPENROUTER: '', GROK: '' });
  const [githubToken, setGithubToken] = useState('');
  const { createConversation } = useConversation();
  const enabledProviders = API_PROVIDERS.filter(p => !p.disabled);
  const disabledProviders = API_PROVIDERS.filter(p => p.disabled);

  useEffect(() => {
    const loadKeys = async () => {
      try {
        await keyStateManager.refreshFromDB();
        const loaded = keyStateManager.getKeys();
        if (loaded) {
          const normalized = { CLAUDE: '', OPENAI: '', GEMINI: '', MISTRAL: '', OPENROUTER: '', GROK: '', ...loaded };
          delete normalized.timestamp;
          setGithubToken(normalized.GITHUB || '');
          delete normalized.GITHUB;
          setKeys(normalized);
        } else {
          setGithubToken('');
        }
      } catch (err) { /* Handle error silently if needed */ }
    };
    loadKeys();
  }, []);

  const handleKeyChange = useCallback((providerId, newValue) => {
    setKeys(prev => ({ ...prev, [providerId]: newValue }));
  }, []);

  return (
    <div className="space-y-4 sm:space-y-6" onClick={(e) => e.stopPropagation()}>
      <div className="p-4 sm:p-5 rounded-xl border border-border-subtle bg-surface-raised">
        <div className="flex items-center gap-2 mb-2">
          <Key className="w-5 h-5 text-primary" />
          <h3 className="text-lg font-medium text-on-surface">Your Keys, Your Control</h3>
        </div>
        <p className="text-md text-on-surface-dim mb-2">This is a Bring Your Own Key (BYOK) environment. Add your API keys to use different language models.</p>
        <p className="text-xs text-on-surface-dim">Keys are encrypted locally and remain encrypted at rest. You must have API credits available on your key to use these services.</p>
      </div>

      <div className="block sm:hidden">
        <div className="space-y-4">
          {enabledProviders.map((provider) => (
            <APIKeyCard key={provider.id} provider={provider} value={keys[provider.id] || ''} onValueChange={(newValue) => handleKeyChange(provider.id, newValue)} createConversation={createConversation} onClose={onClose} />
          ))}
          {disabledProviders.length > 0 && (
            <>
              <div className="py-2 px-4 text-sm font-medium text-on-surface-dim bg-surface-raised rounded-lg border border-border-subtle">Coming Soon</div>
              {disabledProviders.map((provider) => (
                <APIKeyCard key={provider.id} provider={provider} value={keys[provider.id] || ''} onValueChange={(newValue) => handleKeyChange(provider.id, newValue)} createConversation={createConversation} onClose={onClose} />
              ))}
            </>
          )}
        </div>
      </div>

      <div className="hidden sm:block rounded-xl border border-border-subtle overflow-hidden">
        <table className="w-full border-collapse">
          <thead className="bg-surface-raised border-b border-border-subtle">
            <tr>
              <th className="py-3 px-6 text-left text-xs font-medium text-on-surface-dim">Provider</th>
              <th className="py-3 px-4 text-left text-xs font-medium text-on-surface-dim">API Key</th>
              <th className="py-3 px-6 text-left text-xs font-medium text-on-surface-dim">Status</th>
              <th className="py-3 px-6 text-right text-xs font-medium text-on-surface-dim">Actions</th>
            </tr>
          </thead>
          <tbody>
            {enabledProviders.map((provider, index) => (
              <React.Fragment key={provider.id}>
                <APIKeyField provider={provider} value={keys[provider.id] || ''} onValueChange={(newValue) => handleKeyChange(provider.id, newValue)} createConversation={createConversation} onClose={onClose} />
                {index < enabledProviders.length - 1 && (<tr><td colSpan={4} className="border-t border-border-subtle"></td></tr>)}
              </React.Fragment>
            ))}
            {disabledProviders.length > 0 && enabledProviders.length > 0 && (
              <tr className="bg-surface-raised">
                <td colSpan={4} className="py-2 px-6 text-xs font-medium text-on-surface-dim border-t border-b border-border-subtle">Coming Soon</td>
              </tr>
            )}
            {disabledProviders.map((provider, index) => (
               <React.Fragment key={provider.id}>
                <APIKeyField provider={provider} value={keys[provider.id] || ''} onValueChange={(newValue) => handleKeyChange(provider.id, newValue)} createConversation={createConversation} onClose={onClose} />
                {index < disabledProviders.length - 1 && (<tr><td colSpan={4} className="border-t border-border-subtle"></td></tr>)}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <GitHubIntegrationCard storedValue={githubToken} onStoredValueChange={setGithubToken} />

      <CustomEndpointsSection />
    </div>
  );
}

export default APIKeysSection;